import { useRef, useState } from 'react'
import { type Dispatch, type SetStateAction } from 'react'
import { type TFunction } from 'i18next'
import LogTool from '../../../logger/logTools'
import { type SelectOption } from '../../../utils/types'

const log = new LogTool({ context: 'useAutomaticSharesHandler', enable: true, logLevel: 'warn' })

type AutomaticShareFormInput = {
  [inputKey: string]: { contact: SelectOption; customInvitationMessage: string }
}

type AutomaticShareFieldObject = {
  key: string
  type?: 'existingAutomaticShare' | 'newAutomaticShare'
  label: string
}

type AutomaticGroupShareFormInput = {
  [inputKey: string]: { group: SelectOption; customInvitationMessage: string }
}

type AutomaticGroupShareFieldObject = {
  key: string
  type?: 'existingAutomaticGroupShare' | 'newAutomaticGroupShare'
  label: string
}

/**
 * Custom hook that handles all automatic shares related logic for Sharepoint Drawers.
 * This hook manages:
 * - Automatic shares form state
 * - Automatic shares form input state
 * - Unique key generation for automatic share fields
 * - Generating automatic share field objects
 * - Adding and deleting automatic shares
 * - Validating automatic share contacts and custom invitation messages
 *
 * @param t - Translation function
 * @param log - LogTool instance for logging
 * @param isEditMode - Whether the drawer is in edit mode (affects field object type)
 * @returns Object containing automaticSharesForm state, automaticSharesFormInput state, and utility functions
 */
export function useAutomaticSharesHandler(
  t: TFunction,
  log: LogTool,
  isEditMode = false
): {
  automaticSharesForm: any[]
  setAutomaticSharesForm: Dispatch<SetStateAction<any[]>>
  automaticSharesFormInput: AutomaticShareFormInput
  setAutomaticSharesFormInput: Dispatch<SetStateAction<AutomaticShareFormInput>>
  generateAutomaticShareFieldObject: (
    type?: 'existingAutomaticShare' | 'newAutomaticShare'
  ) => AutomaticShareFieldObject
  handleAddAutomaticShare: () => void
  handleDeleteAutomaticShare: (fieldObj: any) => void
  missingAutomaticShareSelect: () => boolean
  customInvitationMessageTooLong: () => boolean
  resetAutomaticShares: () => void
} {
  const uniqueKeyStore = useRef(0)
  const [automaticSharesForm, setAutomaticSharesForm] = useState<any[]>([
    { type: 'addAutomaticShare' },
  ])
  const [automaticSharesFormInput, setAutomaticSharesFormInput] = useState<AutomaticShareFormInput>(
    {}
  )

  /**
   * Generates a unique element key (ascending number).
   * @returns
   */
  function getUniqueKey(): string {
    const key = uniqueKeyStore.current
    uniqueKeyStore.current += 1
    return String(key)
  }

  /**
   * Creates a new, unique AutomaticShareFieldObject that can be inserted into the automaticSharesForm.
   * @param type - Type of automatic share field ('existingAutomaticShare' for edit mode, 'newAutomaticShare' for create mode)
   * @returns
   */
  function generateAutomaticShareFieldObject(
    type: 'existingAutomaticShare' | 'newAutomaticShare' = isEditMode
      ? 'existingAutomaticShare'
      : 'newAutomaticShare'
  ): AutomaticShareFieldObject {
    const fieldObjKey = `automaticShare${getUniqueKey()}`
    return {
      key: fieldObjKey,
      type: type,
      label: t('common:content.label.comment'),
    }
  }

  /**
   * Adds a new automatic share field to the form.
   */
  const handleAddAutomaticShare = () => {
    setAutomaticSharesForm((prev: any) => {
      // we need to store this index to enable the onChange method
      const fieldObjIndex: number = prev.length - 1

      // replace the last element with a new textfield
      prev[fieldObjIndex] = generateAutomaticShareFieldObject('newAutomaticShare')

      // add 'addFormField' entry to the end of formJson
      return [...prev, { type: 'addAutomaticShare' }]
    })
  }

  /**
   * Deletes an automatic share field from the form.
   * @param automaticShareFieldObj - The automatic share field object to delete
   */
  const handleDeleteAutomaticShare = (automaticShareFieldObj: any) => {
    setAutomaticSharesForm((prev: any) => {
      const i = prev.indexOf(automaticShareFieldObj)
      // remove automaticShareFieldObject
      prev.splice(i, 1)

      // delete the according automaticShareFormInput
      setAutomaticSharesFormInput((prev: AutomaticShareFormInput) => {
        const copy = { ...prev }
        // Type guard: only delete when key exists
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete copy[automaticShareFieldObj.key]
        return copy
      })
      return [...prev]
    })
  }

  /**
   * Determines if there are any empty automatic share contact select fields.
   * @returns
   */
  function missingAutomaticShareSelect(): boolean {
    let selectIsMissing = false
    const automaticSharesInputValues: SelectOption[] = Object.values(automaticSharesFormInput).map(
      (autoShare: { contact: SelectOption; customInvitationMessage: string }) => autoShare.contact
    )
    if (automaticSharesForm.length - 1 > automaticSharesInputValues.length) {
      // there is a automaticShareFieldObject that was not yet used to select a automatic share contact
      selectIsMissing = true
    }
    if (automaticSharesInputValues.length > 0) {
      for (const automaticShare of automaticSharesInputValues) {
        if (!automaticShare) {
          selectIsMissing = true
          break
        }
      }
    }
    return selectIsMissing
  }

  /**
   * Determines if there are any customInvitationMessage input fields that contain more than 500 chars.
   * @returns
   */
  function customInvitationMessageTooLong(): boolean {
    let messageTooLong = false
    const customInvitationMessages: string[] = Object.values(automaticSharesFormInput).map(
      (autoShare: { contact: SelectOption; customInvitationMessage: string }) =>
        autoShare.customInvitationMessage
    )
    for (const message of customInvitationMessages) {
      if (message.length > 500) {
        messageTooLong = true
        break
      }
    }
    return messageTooLong
  }

  /**
   * Resets automatic shares to initial state (used in handleClose).
   */
  const resetAutomaticShares = () => {
    uniqueKeyStore.current = 0
    setAutomaticSharesForm([{ type: 'addAutomaticShare' }])
    setAutomaticSharesFormInput({})
  }

  return {
    automaticSharesForm,
    setAutomaticSharesForm,
    automaticSharesFormInput,
    setAutomaticSharesFormInput,
    generateAutomaticShareFieldObject,
    handleAddAutomaticShare,
    handleDeleteAutomaticShare,
    missingAutomaticShareSelect,
    customInvitationMessageTooLong,
    resetAutomaticShares,
  }
}

/**
 * Custom hook that handles all automatic group shares related logic for Sharepoint Drawers.
 * Similar to useAutomaticSharesHandler but for ContactGroups
 *
 * @param t - Translation function
 * @param log - LogTool instance for logging
 * @param isEditMode - Whether the drawer is in edit mode (affects field object type)
 * @returns Object containing automaticGroupSharesForm state, automaticGroupSharesFormInput state, and utility functions
 */
export function useAutomaticGroupSharesHandler(
  t: TFunction,
  log: LogTool,
  isEditMode = false
): {
  automaticGroupSharesForm: any[]
  setAutomaticGroupSharesForm: Dispatch<SetStateAction<any[]>>
  automaticGroupSharesFormInput: AutomaticGroupShareFormInput
  setAutomaticGroupSharesFormInput: Dispatch<SetStateAction<AutomaticGroupShareFormInput>>
  generateAutomaticGroupShareFieldObject: (
    type?: 'existingAutomaticGroupShare' | 'newAutomaticGroupShare'
  ) => AutomaticGroupShareFieldObject
  handleAddAutomaticGroupShare: () => void
  handleDeleteAutomaticGroupShare: (fieldObj: any) => void
  missingAutomaticGroupShareSelect: () => boolean
  customGroupInvitationMessageTooLong: () => boolean
  resetAutomaticGroupShares: () => void
} {
  const uniqueKeyStore = useRef(0)
  const [automaticGroupSharesForm, setAutomaticGroupSharesForm] = useState<any[]>([
    { type: 'addAutomaticGroupShare' },
  ])
  const [automaticGroupSharesFormInput, setAutomaticGroupSharesFormInput] =
    useState<AutomaticGroupShareFormInput>({})

  /**
   * Generates a unique element key (ascending number).
   * @returns
   */
  function getUniqueKey(): string {
    const key = uniqueKeyStore.current
    uniqueKeyStore.current += 1
    return String(key)
  }

  /**
   * Creates a new, unique AutomaticGroupShareFieldObject that can be inserted into the automaticGroupSharesForm.
   * @param type - Type of automatic group share field ('existingAutomaticGroupShare' for edit mode, 'newAutomaticGroupShare' for create mode)
   * @returns
   */
  function generateAutomaticGroupShareFieldObject(
    type: 'existingAutomaticGroupShare' | 'newAutomaticGroupShare' = isEditMode
      ? 'existingAutomaticGroupShare'
      : 'newAutomaticGroupShare'
  ): AutomaticGroupShareFieldObject {
    const fieldObjKey = `automaticGroupShare${getUniqueKey()}`
    return {
      key: fieldObjKey,
      type: type,
      label: t('common:content.label.comment'),
    }
  }

  /**
   * Adds a new automatic group share field to the form.
   */
  const handleAddAutomaticGroupShare = () => {
    setAutomaticGroupSharesForm((prev: any) => {
      // we need to store this index to enable the onChange method
      const fieldObjIndex: number = prev.length - 1

      // replace the last element with a new textfield
      prev[fieldObjIndex] = generateAutomaticGroupShareFieldObject('newAutomaticGroupShare')

      // add 'addFormField' entry to the end of formJson
      return [...prev, { type: 'addAutomaticGroupShare' }]
    })
  }

  /**
   * Deletes an automatic group share field from the form.
   * @param automaticGroupShareFieldObj - The automatic group share field object to delete
   */
  const handleDeleteAutomaticGroupShare = (automaticGroupShareFieldObj: any) => {
    setAutomaticGroupSharesForm((prev: any) => {
      const i = prev.indexOf(automaticGroupShareFieldObj)
      // remove automaticGroupShareFieldObject
      prev.splice(i, 1)

      // delete the according automaticGroupSharesFormInput
      setAutomaticGroupSharesFormInput((prev: AutomaticGroupShareFormInput) => {
        const copy = { ...prev }
        // Type guard: only delete when key exists
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete copy[automaticGroupShareFieldObj.key]
        return copy
      })
      return [...prev]
    })
  }

  /**
   * Determines if there are any empty automatic group share select fields.
   * @returns
   */
  function missingAutomaticGroupShareSelect(): boolean {
    let selectIsMissing = false
    const automaticGroupSharesInputValues: SelectOption[] = Object.values(
      automaticGroupSharesFormInput
    ).map(
      (autoGroupShare: { group: SelectOption; customInvitationMessage: string }) =>
        autoGroupShare.group
    )
    if (automaticGroupSharesForm.length - 1 > automaticGroupSharesInputValues.length) {
      // there is a automaticGroupShareFieldObject that was not yet used to select a automatic group share contact
      selectIsMissing = true
    }
    if (automaticGroupSharesInputValues.length > 0) {
      for (const automaticGroupShare of automaticGroupSharesInputValues) {
        if (!automaticGroupShare) {
          selectIsMissing = true
          break
        }
      }
    }
    return selectIsMissing
  }

  /**
   * Determines if there are any customInvitationMessage input fields that contain more than 500 chars.
   * @returns
   */
  function customGroupInvitationMessageTooLong(): boolean {
    let messageTooLong = false
    const customInvitationMessages: string[] = Object.values(automaticGroupSharesFormInput).map(
      (autoGroupShare: { group: SelectOption; customInvitationMessage: string }) =>
        autoGroupShare.customInvitationMessage
    )
    for (const message of customInvitationMessages) {
      if (message.length > 500) {
        messageTooLong = true
        break
      }
    }
    return messageTooLong
  }

  /**
   * Resets automatic group shares to initial state (used in handleClose).
   */
  const resetAutomaticGroupShares = () => {
    uniqueKeyStore.current = 0
    setAutomaticGroupSharesForm([{ type: 'addAutomaticGroupShare' }])
    setAutomaticGroupSharesFormInput({})
  }

  return {
    automaticGroupSharesForm,
    setAutomaticGroupSharesForm,
    automaticGroupSharesFormInput,
    setAutomaticGroupSharesFormInput,
    generateAutomaticGroupShareFieldObject,
    handleAddAutomaticGroupShare,
    handleDeleteAutomaticGroupShare,
    missingAutomaticGroupShareSelect,
    customGroupInvitationMessageTooLong,
    resetAutomaticGroupShares,
  }
}
